import { useState, useEffect } from 'react'
import fubApi, { FUBUser } from '../services/fubApi'

/**
 * Hook to manage agent selection
 * @param dotloopEmail - Email of the authenticated Dotloop user (used for default selection)
 * @param personId - Optional FUB person ID (fallback for default selection)
 */
export default function useAgents(dotloopEmail?: string | null, personId?: number) {
  const [agents, setAgents] = useState<FUBUser[]>([])
  const [selectedAgent, setSelectedAgent] = useState<FUBUser | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchAgents()
  }, [dotloopEmail, personId])

  const fetchAgents = async () => {
    setLoading(true)
    setError(null)
    
    try {
      // Get agents list
      const agentsList = await fubApi.fetchAgents()
      setAgents(agentsList)
      
      // Priority 1: Match by authenticated Dotloop user's email
      if (dotloopEmail) {
        const emailLower = dotloopEmail.toLowerCase()
        const matchedAgent = agentsList.find(
          agent => agent.email?.toLowerCase() === emailLower
        )
        if (matchedAgent) {
          setSelectedAgent(matchedAgent)
          return
        }
      }
      
      // Priority 2: Fall back to FUB assigned user for the lead
      if (personId) {
        const defaultAgent = await fubApi.getDefaultAgent(personId)
        if (defaultAgent) {
          setSelectedAgent(defaultAgent)
          return
        }
      }
      
      // No match found - leave as null so user must select
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch agents'
      setError(errorMessage)
      console.error('Error fetching agents:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleAgentChange = (agentId: number) => {
    const agent = agents.find(a => a.id === agentId)
    setSelectedAgent(agent || null)
  }

  const refreshAgents = () => {
    fetchAgents()
  }

  return {
    agents,
    selectedAgent,
    loading,
    error,
    handleAgentChange,
    refreshAgents,
  }
}
